// src/api/contractSummaryApi.ts
/**
 * @deprecated RETIRED - not reachable from the UI.
 *
 * Part of the Analysis tab, removed after six months of production data showed a
 * median visit of 3 seconds and only 34 summaries actually run. The tab was also
 * a one-way surface fed by a conversational prompt, so it could not answer the
 * assumption-confirmation questions the model started asking.
 *
 * This is kept because the work behind it may be worth reviving. Nothing in the
 * live UI path reaches it any more, though it still has to typecheck. Reviving
 * it needs the matching backend routes remounted too.
 */
import { ContractSummaryRequestParams } from "../interfaces/contractSummary";
import { createLogger } from "../utils/logger";
import { constructRequest } from "./utils/apiHandler";
import { parseErrorResponse } from "./utils/errorHandler";
import { getErrorMessage } from "../types/errors";
import { createClientErrorInfo } from "./utils/clientErrorHandler";
import { streamErrorToPostHog, ApiNames, ErrorCodes } from "./utils/captureError";

const log = createLogger("ContractSummaryApi");

// Use environment variable for API base URL with fallback
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for API errors
 */
export class ApiStreamingError extends Error {
  errorCode: string;
  statusCode: number;
  requestId?: string;

  constructor(message: string, errorCode: string, statusCode: number, requestId?: string) {
    super(message);
    this.name = "ApiStreamingError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.requestId = requestId;
  }
}

/**
 * Fetches a streaming summary of contract analysis
 * @param file XML file to upload (OOXML content from Word)
 * @param params Optional query parameters
 * @param callbacks Callbacks for streaming response
 * @param accessToken Access token for authentication
 * @returns AbortController to cancel the request if needed
 *
 * @deprecated RETIRED - see the note at the top of this file. Nothing in the
 * live UI path calls this.
 */
export function fetchContractSummaryStream(
  file: File,
  params: ContractSummaryRequestParams,
  callbacks: {
    onStart: () => void;
    onChunk: (chunk: string) => void;
    onComplete: () => void;
    onError: (error: Error) => void;
  },
  accessToken: string
): AbortController {
  // Create abort controller for cancellation
  const abortController = new AbortController();
  // Start the streaming request
  (async () => {
    try {
      // Create form data with the file
      const formData = new FormData();
      formData.append("file", file);

      // Add details if provided
      if (params.details) {
        formData.append("details", JSON.stringify(params.details));
      }

      // Build URL with query parameters
      let url = `${API_BASE_URL}/summary/redline_analyzer/stream/`;

      const queryParams = new URLSearchParams();
      if (params.id) queryParams.append("id", params.id);
      if (params.bypass_cache !== undefined) {
        queryParams.append("bypass_cache", params.bypass_cache.toString());
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += "?" + queryString;
      }

      // Notify that streaming has started
      callbacks.onStart();

      // TEMPORARY DEBUG: Log summary API call
      log.debug("📊 MAKING SUMMARY API CALL:", {
        method: "POST",
        endpoint: url,
        formDataFields: Array.from((formData as any).entries()).map(
          (entry) => (entry as [string, any])[0]
        ),
        hasFile: formData.has("file"),
        queryParams: queryParams.toString(),
      });

      const api_request = constructRequest(
        url,
        "POST",
        accessToken,
        formData,
        abortController.signal
      );

      const response = await fetch(api_request);

      // Shared parser: also runs the 401 refresh bridge so an expired token
      // triggers a token refresh instead of every later call failing the same way.
      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        throw new ApiStreamingError(
          errorData.message || `API error: ${response.status} ${response.statusText}`,
          errorData.error_code || "UNKNOWN_ERROR",
          errorData.status_code,
          errorData.request_id
        );
      }

      // Get the response body as a ReadableStream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Could not get response reader");
      }

      // Read the stream
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            // End of stream
            break;
          }

          // Decode the chunk
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process the buffer to extract complete events
          let lineStart = 0;
          let lineEnd = buffer.indexOf("\n", lineStart);

          while (lineEnd !== -1) {
            const line = buffer.substring(lineStart, lineEnd).trim();
            lineStart = lineEnd + 1;

            // Skip empty lines
            if (!line) {
              lineEnd = buffer.indexOf("\n", lineStart);
              continue;
            }

            // Check if the line starts with 'data: '
            if (line.startsWith("data: ")) {
              try {
                const data = line.substring(6); // Remove 'data: '

                // Handle end of stream marker
                if (data === "[DONE]") {
                  break;
                }

                // Parse the JSON data
                const parsedData = JSON.parse(data);

                // Process the text chunk if available
                if (parsedData.text) {
                  callbacks.onChunk(parsedData.text);
                }
              } catch (e) {
                log.error("Error parsing streaming event data", {
                  error: getErrorMessage(e),
                  rawLine: line.substring(0, 200) + (line.length > 200 ? "..." : ""),
                  lineLength: line.length,
                  bufferPosition: lineStart,
                });
              }
            }

            // Find the next line
            lineEnd = buffer.indexOf("\n", lineStart);
          }

          // Keep unprocessed data in the buffer
          if (lineStart < buffer.length) {
            buffer = buffer.substring(lineStart);
          } else {
            buffer = "";
          }
        }

        // Notify that streaming has completed
        callbacks.onComplete();
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") {
          // Request was aborted, no need to do anything
          return;
        }
        streamErrorToPostHog(e, ApiNames.CONTRACT_SUMMARY_API, ErrorCodes.STREAMING_READ_ERROR);
        throw e;
      } finally {
        reader.releaseLock();
      }
    } catch (error: any) {
      // Handle expected errors
      if (error instanceof ApiStreamingError) {
        streamErrorToPostHog(error, ApiNames.CONTRACT_SUMMARY_API);
        callbacks.onError(error);
      } else if (error instanceof DOMException && error.name === "AbortError") {
        const abortError = new Error("Request was cancelled");
        streamErrorToPostHog(
          abortError,
          ApiNames.CONTRACT_SUMMARY_API,
          ErrorCodes.REQUEST_CANCELLED
        );
        callbacks.onError(abortError);
      } else {
        // Handle client-side errors with helpful, contextual messages
        const clientError = createClientErrorInfo(error, "streaming");
        const apiStreamingError = new ApiStreamingError(
          clientError.message,
          clientError.errorCode,
          0
        );
        streamErrorToPostHog(apiStreamingError, ApiNames.CONTRACT_SUMMARY_API);
        callbacks.onError(apiStreamingError);
      }
    }
  })();

  return abortController;
}
